import { useCallback, useEffect, useState } from 'react';
import { Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { useAuth } from '~/app/auth/context/AuthContext';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { AssetService } from '~/app/services/asset/asset.service';
import type { AssetLookup } from '~/app/services/asset/asset.service';
import {
  AssetTypeService,
  type AssetTypeMetadata,
} from '~/app/services/asset-type/asset-type.service';
import { PartnersService } from '~/app/services/partners/partners.service';
import { PreventiveMaintenanceService } from '~/app/services/preventive-maintenance/preventive-maintenance.service';
import {
  SiteService,
  type SiteLookup,
  type ZoneLookup,
} from '~/app/services/site/site.service';
import type {
  AssetCreateFormValues,
  AssetCreatePhase,
  SelectOption,
} from '../types';
import {
  buildCreateAssetPayload,
  canRetryPlanAssign,
  resolveAssignStartDate,
  resolvePostCreateAction,
  toErrorMessage,
  toPlanSelectOptions,
} from '../utils';
import { useAssetCustomFields } from '~/app/hooks/asset/useAssetCustomFields';

export type UseAssetCreateModalArgs = {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
};

export function useAssetCreateModal({
  visible,
  onCancel,
  onSuccess,
}: UseAssetCreateModalArgs) {
  const { t } = useTranslation('assets');
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const organizationId = user?.organizationId ?? null;
  const canAssignPlan = hasPermission(PermissionKey.MAINTENANCE_MANAGE);

  const [form] = Form.useForm<AssetCreateFormValues>();
  const [phase, setPhase] = useState<AssetCreatePhase>('idle');
  const [createdAssetId, setCreatedAssetId] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  const [assetTypes, setAssetTypes] = useState<AssetTypeMetadata[]>([]);
  const [sites, setSites] = useState<SiteLookup[]>([]);
  const [zones, setZones] = useState<ZoneLookup[]>([]);
  const [assetModels, setAssetModels] = useState<
    { id: string; name: string }[]
  >([]);
  const [parentAssets, setParentAssets] = useState<AssetLookup[]>([]);
  const [landlordOptions, setLandlordOptions] = useState<SelectOption[]>([]);
  const [planOptions, setPlanOptions] = useState<SelectOption[]>([]);

  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState<string | null>(
    null,
  );
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  const {
    fields: customFields,
    saveCustomFieldValues,
    isLoading: customFieldsLoading,
  } = useAssetCustomFields({
    organizationId,
    assetTypeId: selectedAssetTypeId,
    assetId: null,
    enabled: visible,
  });

  const isPartialSuccess = phase === 'partialSuccess';
  const isBusy = phase === 'creating' || phase === 'assigning';
  const assetFieldsDisabled = isPartialSuccess || isBusy;

  const resetModalState = useCallback(() => {
    setPhase('idle');
    setCreatedAssetId(null);
    setAssignError(null);
    setSelectedAssetTypeId(null);
    setSelectedSiteId(null);
    setZones([]);
    setAssetModels([]);
    form.resetFields();
  }, [form]);

  const loadInitialData = useCallback(
    async (orgId: string) => {
      try {
        const [at, s, pa, landlords, plans] = await Promise.all([
          AssetTypeService.findAll(),
          SiteService.findAllByOrganizationId(orgId),
          AssetService.findAllByOrganizationId(orgId),
          PartnersService.listLandlords(orgId).catch(() => null),
          canAssignPlan
            ? PreventiveMaintenanceService.listPlans().catch(() => null)
            : Promise.resolve(null),
        ]);
        setAssetTypes(at);
        setSites(s);
        setParentAssets(pa);
        setLandlordOptions(
          (landlords?.items ?? []).map((item) => ({
            value: item.partnerOrgId,
            label: item.partnerName,
          })),
        );
        setPlanOptions(toPlanSelectOptions(plans?.data ?? []));
      } catch (error) {
        console.error('Failed to load initial data', error);
        message.error(
          t('create.loadDataFailed', 'Failed to load initial data'),
        );
      }
    },
    [canAssignPlan, t],
  );

  useEffect(() => {
    if (visible && organizationId) {
      resetModalState();
      void loadInitialData(organizationId);
      form.setFieldsValue({ planStartDate: dayjs() });
    }
  }, [visible, organizationId, loadInitialData, resetModalState, form]);

  useEffect(() => {
    if (!selectedAssetTypeId) {
      setAssetModels([]);
      return;
    }
    void AssetService.findAssetModelsByAssetTypeId(selectedAssetTypeId)
      .then(setAssetModels)
      .catch((error) => {
        console.error('Failed to load asset models', error);
      });
  }, [selectedAssetTypeId]);

  useEffect(() => {
    if (!selectedSiteId || !organizationId) {
      setZones([]);
      return;
    }
    void SiteService.findZonesBySiteId(organizationId, selectedSiteId)
      .then(setZones)
      .catch((error) => {
        console.error('Failed to load zones', error);
      });
  }, [selectedSiteId, organizationId]);

  const assignPlanToAsset = useCallback(
    async (assetId: string, planId: string, startDate: Date) => {
      setPhase('assigning');
      setAssignError(null);
      try {
        await PreventiveMaintenanceService.assignPlanAssets(planId, {
          assetIds: [assetId],
          startDate,
        });
        message.success(
          t(
            'create.successWithPlan',
            'Asset created and preventive plan assigned',
          ),
        );
        resetModalState();
        onSuccess();
      } catch (error: unknown) {
        console.error('Failed to assign plan to asset', error);
        const errorMessage = toErrorMessage(
          error,
          t('create.assignFailed', 'Failed to assign preventive plan'),
        );
        setAssignError(errorMessage);
        setPhase('partialSuccess');
        message.warning(
          t(
            'create.partialSuccess',
            'Asset was created, but preventive plan assignment failed',
          ),
        );
      }
    },
    [onSuccess, resetModalState, t],
  );

  const handleFinish = useCallback(
    async (values: AssetCreateFormValues) => {
      if (!organizationId) return;
      if (isPartialSuccess && createdAssetId) {
        return;
      }

      setPhase('creating');
      setAssignError(null);

      try {
        const created = await AssetService.create(
          organizationId,
          buildCreateAssetPayload(values),
        );
        setCreatedAssetId(created.id);

        if (customFields.length > 0) {
          await saveCustomFieldValues({
            organizationId,
            assetId: created.id,
            formValues: values.customFields ?? {},
          });
        }

        const next = resolvePostCreateAction({
          planId: values.planId,
          canAssignPlan,
        });
        if (next.kind === 'doneWithoutAssign') {
          message.success(t('create.success', 'Asset created successfully'));
          resetModalState();
          onSuccess();
          return;
        }

        await assignPlanToAsset(
          created.id,
          next.planId,
          resolveAssignStartDate(values.planStartDate),
        );
      } catch (error: unknown) {
        console.error('Failed to create asset', error);
        setPhase('idle');
        message.error(
          toErrorMessage(error, t('create.failed', 'Failed to create asset')),
        );
      }
    },
    [
      assignPlanToAsset,
      canAssignPlan,
      createdAssetId,
      customFields.length,
      isPartialSuccess,
      onSuccess,
      organizationId,
      resetModalState,
      saveCustomFieldValues,
      t,
    ],
  );

  const handleRetryAssign = useCallback(async () => {
    const planId = form.getFieldValue('planId') as string | undefined;
    if (!canRetryPlanAssign({ createdAssetId, planId })) {
      message.error(
        t('create.planRequiredForRetry', 'Select a preventive plan to retry'),
      );
      return;
    }
    await assignPlanToAsset(
      createdAssetId!,
      planId!.trim(),
      resolveAssignStartDate(form.getFieldValue('planStartDate')),
    );
  }, [assignPlanToAsset, createdAssetId, form, t]);

  const handleDoneWithoutPlan = useCallback(() => {
    resetModalState();
    onSuccess();
  }, [onSuccess, resetModalState]);

  const handleModalCancel = useCallback(() => {
    if (createdAssetId) {
      handleDoneWithoutPlan();
      return;
    }
    resetModalState();
    onCancel();
  }, [createdAssetId, handleDoneWithoutPlan, onCancel, resetModalState]);

  const assetTypeOptions: SelectOption[] = assetTypes.map((at) => ({
    label: at.name,
    value: at.id,
  }));
  const assetModelOptions: SelectOption[] = assetModels.map((am) => ({
    label: am.name,
    value: am.id,
  }));
  const siteOptions: SelectOption[] = sites.map((s) => ({
    label: s.name,
    value: s.id,
  }));
  const zoneOptions: SelectOption[] = zones.map((z) => ({
    label: z.name,
    value: z.id,
  }));
  const parentAssetOptions: SelectOption[] = parentAssets.map((pa) => ({
    label: pa.name,
    value: pa.id,
  }));

  return {
    form,
    phase,
    isBusy,
    isPartialSuccess,
    assetFieldsDisabled,
    canAssignPlan,
    assignError,
    createdAssetId,
    assetTypeOptions,
    assetModelOptions,
    siteOptions,
    zoneOptions,
    parentAssetOptions,
    landlordOptions,
    planOptions,
    selectedAssetTypeId,
    selectedSiteId,
    setSelectedAssetTypeId,
    setSelectedSiteId,
    handleFinish,
    handleRetryAssign,
    handleDoneWithoutPlan,
    handleModalCancel,
    customFields,
    customFieldsLoading,
  };
}
